import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { LoaderIcon, ArrowLeft } from "lucide-react";

interface ResetPasswordFormProps {
  onClose: () => void;
  onBack: () => void;
}

export default function ResetPasswordForm({ onClose, onBack }: ResetPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    if (!email) {
      const errorMessage = t("auth.emailRequired") || "Email is required";
      toast({
        title: t("auth.error") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: "https://goodvote.ge/reset-password",
      });

      if (error) {
        if (error.message.includes("User not found")) {
          toast({
            title: t("auth.error") || "Error",
            description: t("auth.emailNotRegistered") || "This email is not registered. Please use the email you signed up with.",
            variant: "destructive",
          });
        } else {
          throw error;
        }
      } else {
        setEmailSent(true);
        toast({
          title: t("auth.success") || "Success",
          description: t("auth.resetEmailSent") || "Password reset email has been sent! Check your email inbox.",
        });
      }
    } catch (error: any) {
      const errorMessage = error.message || "Failed to send reset email";
      toast({
        title: t("auth.error") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  if (emailSent) {
    return (
      <div className="space-y-4 text-center">
        <h3 className="text-lg font-semibold text-foreground">
          {t("auth.emailSent") || "Email Sent!"}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("auth.checkEmailInbox") || "Please check your email inbox and follow the instructions to reset your password."}
        </p>
        <Button onClick={onClose} className="w-full">
          {t("auth.close") || "Close"}
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="p-1"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold text-foreground">
          {t("auth.resetPassword") || "Reset Password"}
        </h3>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        {t("auth.enterEmailForReset") || "Enter your email address and we'll send you a link to reset your password."}
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="reset-email" className="block text-sm font-medium mb-1">
            {t("auth.email") || "Email"}
          </label>
          <Input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder={t("auth.emailPlaceholder") || "your@email.com"}
          />
        </div>
        
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
              {t("auth.sending") || "Sending..."}
            </>
          ) : (
            t("auth.resetPassword") || "Reset Password"
          )}
        </Button>
      </form>
    </div>
  );
}